/**
 * SYI Page Performance Monitor
 * Collects timing metrics from HTML request to LCP completion
 */

export interface SyiPerformanceMetrics {
    // Navigation Timing
    ttfb: number;
    htmlDownload: number;
    domParse: number;
    domContentLoaded: number;
    domComplete: number;

    // Resource Timing (JS files)
    jsLoadShell: number;
    jsLoadSyi: number;
    jsLoadVendors: number;

    // React Hydration
    hydrationStart: number;
    hydrationEnd: number;
    hydrationDuration: number;

    // Paint Timing
    fp: number;
    fcp: number;
    lcp: number;
    lcpElement: string;

    // Metadata
    appViewDevice: string;
    pageType: string;
    categoryId: string;
    timestamp: number;
    url: string;
}

type PartialMetrics = Partial<SyiPerformanceMetrics>;

const REPORT_ENDPOINT = '/bff-api/metrics/performance';
const LCP_WAIT_TIMEOUT = 5000;

class SyiPerformanceMonitor {
    private metrics: PartialMetrics = {};
    private isCollecting = false;
    private lcpObserver: PerformanceObserver | null = null;
    private reported = false;

    start(options: { pageType: string; appViewDevice: string; categoryId?: string }): void {
        if (typeof window === 'undefined' || this.isCollecting) {
            return;
        }

        this.reset();
        this.isCollecting = true;

        this.metrics = {
            pageType: options.pageType,
            appViewDevice: options.appViewDevice || 'web',
            categoryId: options.categoryId || '',
            timestamp: Date.now(),
            url: window.location.pathname,
        };

        this.collectNavigationTiming();
        this.collectPaintTiming();
        this.observeLCP();

        // Collect resource timing after DOM is ready
        if (document.readyState === 'complete') {
            this.collectResourceTiming();
        } else {
            window.addEventListener('load', () => this.collectResourceTiming(), { once: true });
        }
    }

    private reset(): void {
        this.metrics = {};
        this.isCollecting = false;
        this.reported = false;
        if (this.lcpObserver) {
            this.lcpObserver.disconnect();
            this.lcpObserver = null;
        }
    }

    private collectNavigationTiming(): void {
        try {
            const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
            if (entries.length > 0) {
                const nav = entries[0];
                this.metrics.ttfb = Math.round(nav.responseStart - nav.requestStart);
                this.metrics.htmlDownload = Math.round(nav.responseEnd - nav.responseStart);
                this.metrics.domParse = Math.round(nav.domInteractive - nav.responseEnd);
                this.metrics.domContentLoaded = Math.round(nav.domContentLoadedEventEnd - nav.startTime);
                this.metrics.domComplete = Math.round(nav.domComplete - nav.startTime);
            }
        } catch (_e) {
            // Silently fail if Navigation Timing API is not available
        }
    }

    private collectResourceTiming(): void {
        try {
            const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];

            for (const resource of resources) {
                const name = resource.name;
                const duration = Math.round(resource.responseEnd - resource.startTime);

                if (name.includes('shell.') && name.endsWith('.js')) {
                    this.metrics.jsLoadShell = duration;
                } else if (name.includes('syi.') && name.endsWith('.js')) {
                    this.metrics.jsLoadSyi = duration;
                } else if (name.includes('vendors-shell.') && name.endsWith('.js')) {
                    this.metrics.jsLoadVendors = duration;
                }
            }
        } catch (_e) {
            // Silently fail
        }
    }

    private collectPaintTiming(): void {
        try {
            const paintEntries = performance.getEntriesByType('paint');

            for (const entry of paintEntries) {
                if (entry.name === 'first-paint') {
                    this.metrics.fp = Math.round(entry.startTime);
                } else if (entry.name === 'first-contentful-paint') {
                    this.metrics.fcp = Math.round(entry.startTime);
                }
            }
        } catch (_e) {
            // Silently fail
        }
    }

    private observeLCP(): void {
        if (!('PerformanceObserver' in window)) {
            return;
        }

        try {
            this.lcpObserver = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                if (entries.length > 0) {
                    const lastEntry = entries[entries.length - 1] as any;
                    this.metrics.lcp = Math.round(lastEntry.startTime);
                    this.metrics.lcpElement = this.getElementSelector(lastEntry.element);
                }
            });

            this.lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
        } catch (_e) {
            // Silently fail
        }
    }

    private getElementSelector(element: Element | null): string {
        if (!element) {
            return 'unknown';
        }

        const tagName = element.tagName?.toLowerCase() || 'unknown';
        const id = element.id ? `#${element.id}` : '';
        const className =
            element.className && typeof element.className === 'string'
                ? `.${element.className.split(' ').filter(Boolean).slice(0, 2).join('.')}`
                : '';

        return `${tagName}${id}${className}`.slice(0, 100);
    }

    markHydrationStart(): void {
        if (!this.isCollecting) {
            return;
        }
        this.metrics.hydrationStart = Math.round(performance.now());
    }

    markHydrationEnd(): void {
        if (!this.isCollecting) {
            return;
        }
        this.metrics.hydrationEnd = Math.round(performance.now());

        if (this.metrics.hydrationStart !== undefined) {
            this.metrics.hydrationDuration = this.metrics.hydrationEnd - this.metrics.hydrationStart;
        }
    }

    scheduleReport(delay: number = LCP_WAIT_TIMEOUT): void {
        if (!this.isCollecting || this.reported) {
            return;
        }

        setTimeout(() => {
            this.report();
        }, delay);
    }

    report(): void {
        if (!this.isCollecting || this.reported) {
            return;
        }

        this.reported = true;

        // Disconnect LCP observer before reporting
        if (this.lcpObserver) {
            this.lcpObserver.disconnect();
            this.lcpObserver = null;
        }

        // Final collection
        this.collectResourceTiming();
        this.collectPaintTiming();

        const payload = JSON.stringify({
            eventName: 'syi_page_performance',
            data: this.metrics,
        });

        fetch(REPORT_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
        }).catch(() => {
            // Ignore errors
        });
    }

    getMetrics(): PartialMetrics {
        return { ...this.metrics };
    }

    isActive(): boolean {
        return this.isCollecting;
    }
}

export const syiPerformanceMonitor = new SyiPerformanceMonitor();
